import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, User, Trash, PencilSimple, UploadSimple, X, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import ImportCustomersModal from "../components/ImportCustomersModal";
import { CustomerAvatar } from "../components/CustomerAvatar";

const EMPTY = { name: "", phone: "", email: "", notes: "" };

export default function Customers() {
  // Honour `?q=…` in the URL so we can deep-link into the page pre-filtered
  // (e.g. from a legacy ticket whose customer_id is missing but phone exists).
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [matchCount, setMatchCount] = useState(null); // matches for current query (server-side)
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { user } = useAuth();

  const PAGE_SIZE = 100;
  const reqIdRef = useRef(0);

  // Initial load (once): first 100 + total. 20k+ customer address books used
  // to ship every row on page load (~200 KB) — now capped.
  useEffect(() => {
    let cancelled = false;
    api.get("/customers/count").then((r) => !cancelled && setTotal(r.data.total));
    api.get("/customers", { params: { limit: PAGE_SIZE } }).then((r) => {
      if (cancelled) return;
      setItems(r.data);
      setHasMore(r.data.length >= PAGE_SIZE);
    });
    return () => { cancelled = true; };
  }, []);

  // Debounced server search — only fires when user types. Fetches matching rows + count.
  useEffect(() => {
    const query = q.trim();
    const myId = ++reqIdRef.current;

    // Keep the URL query-param in sync so refreshing / sharing the URL
    // preserves the filter.
    setSearchParams(query ? { q: query } : {}, { replace: true });

    if (!query) {
      setMatchCount(null);
      setSearching(false);
      // Reload base page when cleared
      api.get("/customers", { params: { limit: PAGE_SIZE } }).then((r) => {
        if (reqIdRef.current === myId) {
          setItems(r.data);
          setHasMore(r.data.length >= PAGE_SIZE);
        }
      });
      return;
    }

    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const [listRes, countRes] = await Promise.all([
          api.get("/customers", { params: { q: query, limit: PAGE_SIZE } }),
          api.get("/customers/count", { params: { q: query } }),
        ]);
        if (reqIdRef.current !== myId) return; // stale request, ignore
        setItems(listRes.data);
        setMatchCount(countRes.data.total);
        setHasMore(listRes.data.length >= PAGE_SIZE && countRes.data.total > listRes.data.length);
      } catch (e) {
        if (reqIdRef.current === myId) toast.error(formatApiError(e));
      } finally {
        if (reqIdRef.current === myId) setSearching(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [q]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get("/customers", {
        params: { limit: PAGE_SIZE, skip: items.length, q: q.trim() || undefined },
      });
      const next = r.data || [];
      setItems((prev) => [...prev, ...next]);
      const loaded = items.length + next.length;
      const cap = matchCount ?? total ?? Infinity;
      setHasMore(next.length >= PAGE_SIZE && loaded < cap);
    } finally {
      setLoadingMore(false);
    }
  };

  const reloadAll = async () => {
    const [listRes, countRes] = await Promise.all([
      api.get("/customers", { params: { limit: PAGE_SIZE, q: q.trim() || undefined } }),
      api.get("/customers/count"),
    ]);
    setItems(listRes.data);
    setTotal(countRes.data.total);
    setHasMore(listRes.data.length >= PAGE_SIZE);
  };

  const open = (c) => {
    if (c) { setEditing(c); setForm({ name: c.name, phone: c.phone || "", email: c.email || "", notes: c.notes || "" }); }
    else { setEditing(null); setForm(EMPTY); }
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      setShowModal(false);
      await reloadAll();
      toast.success(editing ? "Customer updated" : "Customer added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); await reloadAll(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Stable total display (doesn't flicker during search)
  const totalDisplay = total ?? items.length;
  const hasQuery = q.trim().length > 0;

  return (
    <Layout>
      <PageHeader
        overline="CUSTOMERS"
        title={
          <span className="flex items-baseline gap-3 flex-wrap">
            Address book
            <span
              data-testid="customers-total-count"
              className="text-[13px] font-mono font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full tabular-nums"
            >
              {totalDisplay.toLocaleString()} {totalDisplay === 1 ? "contact" : "contacts"}
            </span>
            {/* Reserved slot — always present, avoids layout jump */}
            <span className="text-[12px] font-mono min-h-[22px] inline-flex items-center">
              {hasQuery && (
                <span className="text-[#1E5FEB] bg-blue-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  {searching ? (
                    <>
                      <CircleNotch size={11} weight="bold" className="animate-spin" />
                      searching…
                    </>
                  ) : (
                    <>
                      {(matchCount ?? items.length).toLocaleString()} match
                      {(matchCount ?? items.length) === 1 ? "" : "es"}
                    </>
                  )}
                </span>
              )}
            </span>
          </span>
        }
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="customers-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name / phone / email"
                className="w-72 h-11 pl-9 pr-9 border border-[#E5E7EB] bg-white focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
                  aria-label="Clear search"
                  data-testid="customers-search-clear"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
            <button onClick={() => setShowImport(true)} data-testid="import-customers-btn" className="h-11 px-4 border border-[#121216] text-[13px] font-semibold flex items-center gap-2 hover:bg-[#121216] hover:text-white transition-colors">
              <UploadSimple size={14} weight="bold" /> IMPORT CSV
            </button>
            <button onClick={() => open(null)} data-testid="add-customer-btn" className="h-11 px-4 bg-[#1E5FEB] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1849C8]">
              <Plus size={14} weight="bold" /> ADD CUSTOMER
            </button>
          </>
        }
      />

      <div className="p-6">
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="p-3">Name</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Email</th>
                <th className="p-3">Notes</th>
                <th className="p-3 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]" data-testid={`customer-row-${c.id}`}>
                  <td className="p-3 font-medium">
                    <Link to={`/customers/${c.id}`} className="hover:underline inline-flex items-center gap-2.5" data-testid={`customer-link-${c.id}`}>
                      <CustomerAvatar seed={c.id} size={30} testId={`customer-avatar-${c.id}`} />{c.name}
                    </Link>
                  </td>
                  <td className="p-3 font-mono text-xs">{c.phone || "—"}</td>
                  <td className="p-3 text-zinc-600">{c.email || "—"}</td>
                  <td className="p-3 text-zinc-500 truncate max-w-xs">{c.notes || "—"}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => open(c)} className="p-2 border border-[#E5E7EB] hover:border-[#121216]"><PencilSimple size={14} /></button>
                      {user?.role === "admin" && (
                        <button onClick={() => del(c)} className="p-2 border border-[#E5E7EB] hover:border-[#DC2626] hover:text-[#DC2626]"><Trash size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="5" className="p-12 text-center">
                  <User size={36} weight="duotone" className="mx-auto text-zinc-300 mb-2" />
                  <div className="text-sm text-zinc-500">
                    {hasQuery ? `No customers match "${q}".` : "No customers yet."}
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>

          {/* Pagination footer — server-side pagination keeps the initial
              payload small (~100 rows) even on 20k+ contact books. */}
          {items.length > 0 && (
            <div className="px-4 py-3 border-t border-[#E5E7EB] text-[11px] text-[#9CA3AF] font-mono flex items-center justify-between bg-[#FAFBFC]">
              <span>
                {hasQuery
                  ? `Showing ${items.length} of ${(matchCount ?? items.length).toLocaleString()} matches`
                  : `Showing ${items.length} of ${(total ?? items.length).toLocaleString()} contacts`}
              </span>
              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="customers-load-more"
                  className="text-[11px] font-semibold tracking-wider uppercase text-[#1E5FEB] hover:text-[#1849C8] disabled:text-[#9CA3AF] transition-colors"
                >
                  {loadingMore ? "LOADING…" : `LOAD ${PAGE_SIZE} MORE →`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-xl border border-[#E5E7EB]" data-testid="customer-modal">
            <div className="border-b border-[#E5E7EB] p-5">
              <div className="overline mb-1">{editing ? "EDIT" : "ADD"} CUSTOMER</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight">{editing ? form.name : "New customer"}</h2>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} data-testid="cust-name" /></Field></div>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fld + " font-mono"} data-testid="cust-phone" /></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fld} /></Field>
              <div className="col-span-2"><Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`${fld} h-auto py-2`} /></Field></div>
            </div>
            <div className="flex border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-12 text-sm font-semibold">CANCEL</button>
              <button type="submit" data-testid="cust-save" className="flex-1 h-12 bg-[#121216] hover:bg-[#1E5FEB] text-white text-sm font-semibold">SAVE</button>
            </div>
          </form>
        </div>
      )}

      <ImportCustomersModal open={showImport} onClose={() => setShowImport(false)} onImported={() => reloadAll()} />
    </Layout>
  );
}

const fld = "w-full h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none bg-white";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}
